import React, { useEffect, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import styled from 'styled-components';

import Input from 'components/lib/Input';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { SelectAdvancedFilterOptionType } from 'types/common';
import { useDebounce } from 'utils/hooks/useDebounce';

import { useRingCentralRecordsContext } from './RingCentralRecordsContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 30px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
`;

const SearchInput = styled(Input)`
  width: 500px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SOURCE_TYPE_OPTIONS = [
  { label: 'Call', value: 'call' },
  { label: 'SMS', value: 'sms' },
  { label: 'Voicemail', value: 'voicemail' },
  { label: 'Fax', value: 'fax' },
  { label: 'Unknown', value: 'unknown' },
];

const DIRECTION_OPTIONS = [
  { label: 'Inbound', value: 'inbound' },
  { label: 'Outbound', value: 'outbound' },
  { label: 'Unknown', value: 'unknown' },
];

const MATCHING_STATUS_OPTIONS = [
  { label: 'Matched', value: 'matched' },
  { label: 'Unmatched', value: 'unmatched' },
  { label: 'Ambiguous', value: 'ambiguous' },
  { label: 'Ignored', value: 'ignored' },
];

const MEDIA_TYPE_OPTIONS = [
  { label: 'ACE Insights', value: 'ace_insights' },
  { label: 'Audio Recording', value: 'audio_recording' },
  { label: 'Audio Transcription', value: 'audio_transcription' },
  { label: 'Call Recording', value: 'call_recording' },
  { label: 'Fax', value: 'fax' },
  { label: 'MMS Attachment', value: 'mms_attachment' },
  { label: 'Rendered Document', value: 'rendered_document' },
  { label: 'Text', value: 'text' },
  { label: 'Unknown', value: 'unknown' },
];

const getValues = (selection: SelectAdvancedFilterOptionType[]) => selection.map((item) => String(item.value));

const RingCentralRecordsControls = () => {
  const { filters, setFilters } = useRingCentralRecordsContext();
  const [search, setSearch] = useState(filters.search);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setSearch(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setFilters({ search: debouncedSearch });
  }, [debouncedSearch, setFilters]);

  return (
    <Container>
      <TopRow>
        <SearchInput
          value={search}
          prefix={<SearchOutlined />}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
          placeholder="Search records, phone numbers, names, sessions, or media text"
        />
      </TopRow>
      <FiltersRow>
        <SelectAdvancedFilter
          className={classNames({ active: filters.source_type.length > 0 })}
          placeholder="Type"
          options={SOURCE_TYPE_OPTIONS}
          defaultValue={filters.source_type}
          onSelect={(selection: SelectAdvancedFilterOptionType[]) => setFilters({ source_type: getValues(selection) })}
        />
        <SelectAdvancedFilter
          className={classNames({ active: filters.direction.length > 0 })}
          placeholder="Direction"
          options={DIRECTION_OPTIONS}
          defaultValue={filters.direction}
          onSelect={(selection: SelectAdvancedFilterOptionType[]) => setFilters({ direction: getValues(selection) })}
        />
        <SelectAdvancedFilter
          className={classNames({ active: filters.matching_status.length > 0 })}
          placeholder="Match"
          options={MATCHING_STATUS_OPTIONS}
          defaultValue={filters.matching_status}
          onSelect={(selection: SelectAdvancedFilterOptionType[]) =>
            setFilters({ matching_status: getValues(selection) })
          }
        />
        <SelectAdvancedFilter
          className={classNames({ active: filters.media_type.length > 0 })}
          placeholder="Media"
          options={MEDIA_TYPE_OPTIONS}
          defaultValue={filters.media_type}
          onSelect={(selection: SelectAdvancedFilterOptionType[]) => setFilters({ media_type: getValues(selection) })}
        />
      </FiltersRow>
    </Container>
  );
};

export default RingCentralRecordsControls;
